import React, { useContext, useEffect, useRef, useState } from "react";
import Logo from "@components/Logo/Logo";
import FatsuLogo from "@assets/images/logo.webp";
import Navbar from "@components/Navbar/Navbar";
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import modalBorder from "@assets/images/borders/modalBorder.webp";
import Button from "@components/Button/Button";
import { AuthContext } from "@context/AuthContext";
import Signup from "@components/Signup/Signup";
import Login from "@components/Login/Login";
import routes from "@routes/routes";
import LangButton from "@components/LangButton/LangButton";
import { LoginContext } from "@context/LoginContext";
import useResizeObserver from "@hooks/useResizeObserver";
import ForgotPassowrd from "@components/ForgotPassword/ForgotPassword";
import NewPasswordForm from "@components/ForgotPassword/NewPasswordForm";
import profileimg from "@assets/images/profile-dummy-user.webp";
import { UserContext } from "@src/context/UserContext";
import LogoutModal from "@components/Logout/LogoutModal";
import UserDropdown from "@components/Header/UserDropdown";
import NotificationWrapper from "@components/Header/NotificationWrapper";
import { getDb } from "@src/db/EpisodesDb";
import { TotalItemsContext } from "@src/context/TotalItemsContext";
import ModalBorderLarge from "@assets/images/borders/ModalBorderLarge.webp";
import sendMail from "@assets/images/sendMail.svg";
import useOnclickOutside from "react-cool-onclickoutside";
import { SignupContext } from "@src/context/SignupContext";
import useApiHook from "@src/hooks/useApiHook";
import useApiError from "@src/hooks/useApiError";
import { formatItemsCount } from "@src/utils/CartItemsCountsHelper";
import Cookies from "js-cookie";
import useNotification from "@src/hooks/useNotification";
import useDownloadManager from "@src/hooks/useDownloadManager";
import type { IAudio, IEpisode } from "@src/types/AppTypes";
import { getImageUrlWithExpiry } from "@src/utils/ImageExpirtyHelper";
import useIsMobile from "@src/hooks/useIsMobile";
import useVideoManager from "@src/hooks/useVideoManager";

const Header: React.FC = () => {
  const videoManager = useVideoManager();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const [isIphone] = useState(
    /iPhone/.test(navigator?.userAgent) || /iPhone/.test(navigator?.platform)
  );
  const [isDbReady, setIsDbReady] = useState(false);
  const user = useContext(UserContext);
  const setApiError = useApiError();
  const [myaccountDropdown, setmyaccountDropdown] = useState(false);

  const [isConfirmationLoading, setIsConfirmationLoading] =
    useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [isResentModalOpen, setIsResentModalOpen] = useState<boolean>(false);
  const totalCartItems = useContext(TotalItemsContext);
  const flashNotification = useNotification();
  const downloadManager = useDownloadManager();
  const [forgotModal, setForgotModal] = useState<boolean>(false);
  const location = useLocation();
  const isMobileView = useResizeObserver();
  const navigate = useNavigate();
  const { login, setLogin } = useContext(LoginContext);
  const auth = useContext(AuthContext);
  const { setSignup, signup } = useContext(SignupContext);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [openHeader, setOpenHeader] = useState(false);
  const [showUpdatePasswordForm, setShowUpdatePasswordForm] =
    useState<boolean>(false);
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);
  const [isUserProfileImageLoading, setIsUserProfileImageLoading] =
    useState<boolean>(true);
  // for close login modal on outside click
  const loginModalRef = useRef<HTMLDivElement>(null);
  useOnclickOutside(
    () => {
      document
        .getElementById("login-form-container")
        ?.classList.add("form-shake");
    },
    {
      refs: [loginModalRef],
      ignoreClass: [
        "swal2-container",
        "resend-modal",
        "login-form",
        "signup-form",
        "forgot-password-form",
      ],
    }
  );

  // for close login modal on outside click
  const resendModalref = useRef<HTMLDivElement>(null);
  useOnclickOutside(
    () => {
      setIsResentModalOpen(false);
    },
    {
      refs: [resendModalref],
      ignoreClass: [
        "swal2-container",
        "resend-modal",
        "login-form",
        "signup-form",
        "forgot-password-form",
      ],
    }
  );

  // for close signup modal on outside click
  const signupModalRef = useRef<HTMLDivElement>(null);
  useOnclickOutside(
    () => {
      document.getElementById("sign-up-form")?.classList.add("form-shake");
    },
    {
      refs: [signupModalRef],
      ignoreClass: [
        "swal2-container",
        "login-form",
        "signup-form",
        "forgot-password-form",
      ],
    }
  );

  // for close forgot password modal on outside click
  const forgotPasswordModalRef = useRef<HTMLDivElement>(null);
  useOnclickOutside(
    () => {
      document
        .getElementById("forgot-password-form")
        ?.classList.add("form-shake");
    },
    {
      refs: [forgotPasswordModalRef],
      ignoreClass: [
        "swal2-container",
        "login-form",
        "signup-form",
        "forgot-password-form",
      ],
    }
  );

  const resentConfirmationEndpoint = useApiHook({
    url: "/users/confirmation",
    method: "post",
  });

  // for close new password form on outside click
  const newPasswordModalRef = useRef<HTMLDivElement>(null);
  useOnclickOutside(
    () => {
      document
        .getElementById("reset-password-form")
        ?.classList.add("form-shake");
    },
    {
      refs: [newPasswordModalRef],
      ignoreClass: [
        "swal2-container",
        "login-form",
        "signup-form",
        "forgot-password-form",
      ],
    }
  );

  const resendConfirmationEmail = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();

    try {
      setIsConfirmationLoading(true);
      const payload = {
        email,
      };
      const res = await resentConfirmationEndpoint<{
        status: boolean;
        message: string;
      }>(payload, "", "");
      if (res && res.status === 200) {
        flashNotification({ status: true, message: res.data.message });
        setIsResentModalOpen(false);
      }
    } catch (e: any) {
      if (e.status === 400) {
        flashNotification({ status: false, message: e.response.data.error });
        setIsResentModalOpen(false);
        return;
      }
      setApiError(e);
    } finally {
      setIsConfirmationLoading(false);
    }
  };

  useEffect(() => {
    if (location?.state?.showUpdatePasswordForm === true) {
      setShowUpdatePasswordForm(true);
    }
    return () => {
      setIsDropdownOpen(false);
    };
  }, [location?.state?.showUpdatePasswordForm]);

  useEffect(() => {
    if (!location.pathname.includes("episode")) {
      localStorage.removeItem("selectedEpisode");
      localStorage.removeItem("currentSeason");
      localStorage.removeItem("currentSeasonEpisodes");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (openHeader) {
      window.document.body.style.overflow = "hidden";
    }

    return () => {
      window.document.body.style.overflow = "auto";
    };
  }, [openHeader]);

  useEffect(() => {
    if (!navigator.onLine && !isIphone) {
      if (!location.pathname.includes("/episode/")) {
        if (Cookies.get("aT")) {
          navigate(routes.downloads);
        } else {
          navigate(routes.default);
        }
      }
    }
  }, [location.pathname, navigate, isIphone]);

  //restart the downloads on page load (episodes)
  useEffect(() => {
    if (!isDbReady) return;
    const startEpisodeDownloads = async () => {
      const downloadingEpisodes = await getDb()?.downloadingVideos?.toArray();
      if (downloadManager && downloadingEpisodes?.length) {
        downloadingEpisodes.forEach(async (item) => {
          const episode = await getDb()?.episodes?.get(item.id);
          if (episode) {
            downloadManager.addEpisodeDownload(
              episode as unknown as IEpisode,
              item.tsUrls,
              item.end,
              item.progress,
              item.isDownloading,
              item.parts
            );
          } else {
            await getDb()
              ?.episodesBlobData.where("fileId")
              .equals(item.id)
              .delete();
            await getDb()?.downloadingVideos.delete(item.id);
            await getDb()?.episodes.delete(item.id);
          }
        });
      }
    };
    startEpisodeDownloads();
  }, [downloadManager, isDbReady]);

  //restart the downloads on page load (music)
  useEffect(() => {
    if (!isDbReady) return;
    const startMusicDownloads = async () => {
      const downloadingMusic = await getDb()?.downloadingMusics?.toArray();
      if (downloadManager && downloadingMusic?.length) {
        downloadingMusic.forEach(async (item) => {
          const music = await getDb()?.musics?.get(item.id);
          if (music) {
            downloadManager.addMusicDownload(
              music as unknown as IAudio,
              item.end,
              item.progress,
              item.isDownloading
            );
          } else {
            await getDb()
              ?.musicBlobData.where("fileId")
              .equals(item.id)
              .delete();
            await getDb()?.downloadingMusics.delete(item.id);
            await getDb()?.musics.delete(item.id);
          }
        });
      }
    };
    startMusicDownloads();
  }, [downloadManager, isDbReady]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (getDb()) {
        downloadManager.isReady.setIsReady(true);
        clearInterval(timer);
      }
    }, 100);
    downloadManager.isReady.subscribe(() => {
      if (downloadManager.isReady.getIsReady()) {
        downloadManager.getAndSetPendingDownloads(() => {
          setIsDbReady(true);
        });
      }
    });
  }, [downloadManager]);

  //sync data base in video manager for downloaded episodes
  useEffect(() => {
    if (!downloadManager || !videoManager) return;

    downloadManager.downloadingEpisodes.subscribe(async () => {
      await videoManager.getDownloadedVideos();
    });
  }, [downloadManager, videoManager]);

  return (
    <>
      <div
        className={`${
          !isDropdownOpen ? "hidden" : ""
        } absolute left-0 top-0 w-[99vw] h-screen bg-transparent z-[2]`}
        onClick={(e) => {
          e.preventDefault();
          setIsDropdownOpen(!isDropdownOpen);
        }}
      ></div>

      <header
        id="header"
        className="max-md:sticky max-md:top-0 bg-blue relative max-md:z-[99]"
      >
        <div
          id="header-component"
          className="container flex items-center justify-between mx-auto relative py-5 max-lg:p-4"
        >
          <div className="flex items-center gap-3 max-sm:gap-1">
            <button
              onClick={() => setOpenHeader(!openHeader)}
              className="text-primary-gradient text-3xl duration-150 hover:scale-95 max-md:text-2xl hidden max-xl:block"
              aria-label="menu-button"
            >
              <i className="ri-menu-line"></i>
            </button>
            <Logo
              onClick={() => {
                if (window.location.pathname === "/") window.location.reload();
                else navigate(routes.default);
              }}
              src={FatsuLogo}
              alt="App-Logo"
              className="w-44 object-contain max-md:w-24 cursor-pointer"
            />
          </div>
          <Navbar className="bg-no-repeat bg-100 max-xl:bg-contain bg-center flex items-center justify-center gap-8 max-2xl:gap-7 py-8 px-10 max-2xl:px-8 max-xl:hidden" />
          {!auth && !isMobileView && (
            <div className="flex items-center max-xl:hidden gap-2 justify-end min-w-[255px]">
              <Button
                onClick={() => {
                  setLogin({ type: "updateLogin", payload: true });
                  if (isMobile) {
                    searchParams.set("tab", "login");
                    setSearchParams(searchParams, { replace: true });
                  }
                }}
                title="Login"
                className="text-primary p-1 duration-150 hover:scale-95 text-xl"
              />
              <Button
                onClick={() => {
                  setSignup({ type: "updateSignup", payload: true });
                  if (isMobile) {
                    searchParams.set("tab", "signup");
                    setSearchParams(searchParams, { replace: true });
                  }
                }}
                title="Sign Up"
                className="text-primary p-1 duration-150 hover:scale-95 text-xl"
              />
              <div className="relative">
                <Link
                  to={routes.cart}
                  className="w-14 h-14 max-xl:w-[48px] max-xl:h-[48px] grid place-items-center border-2 text-2xl max-xl:text-xl border-[#1D1159] rounded-full bg-radial-gradient duration-150 hover:scale-95"
                  aria-label="Shopping cart"
                >
                  <i className="ri-shopping-cart-2-line"></i>
                </Link>
                {totalCartItems > 0 && (
                  <span
                    role="button"
                    onClick={() => {
                      navigate(routes.cart);
                    }}
                    className="absolute family-mochiy-pop -top-1 -right-1 text-base w-7 h-7 max-md:w-[22px] max-md:h-[22px] bg-primary rounded-full flex items-center justify-center"
                    aria-label="Total Item"
                  >
                    {formatItemsCount(totalCartItems)}
                  </span>
                )}
              </div>
              <LangButton
                showLangButton={!auth}
                mobileView={!isMobileView}
                buttonSytles="text-white cursor-pointer flex justify-center relative mx-auto w-8 h-8 z-[1]"
                iconStyles="ri-global-line absolute -z-10 w-full h-full top-[-6px] text-[32px]"
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            {auth && (
              <div className="flex items-center gap-2">
                <div className="flex items-center relative">
                  <h3 className="text-white text-xl truncate max-w-[90px] mr-2 max-xl:hidden">
                    {user?.first_name ? user.first_name : ""}
                  </h3>
                  <button
                    className="rounded-full w-[52px] h-[52px] max-xl:w-12 max-xl:h-12 duration-150 hover:scale-95 overflow-hidden border-2 border-[#9c2682]"
                    onClick={(e) => {
                      e.preventDefault();
                      if (window.innerWidth < 1280) {
                        navigate(routes.account);
                      } else {
                        setIsDropdownOpen(!isDropdownOpen);
                      }
                    }}
                    aria-label="Profile"
                  >
                    {isUserProfileImageLoading && (
                      <div className="skeleton w-full h-full !rounded-full"></div>
                    )}
                    <img
                      src={
                        user?.profile
                          ? getImageUrlWithExpiry(user.profile)
                          : profileimg
                      }
                      className="w-full h-full object-cover rounded-full"
                      onLoad={() => {
                        setIsUserProfileImageLoading(false);
                      }}
                      onError={() => {
                        setIsUserProfileImageLoading(false);
                      }}
                      alt="profile-img"
                    />
                  </button>
                  <UserDropdown
                    isDropdownOpen={isDropdownOpen}
                    setIsDropdownOpen={setIsDropdownOpen}
                    setShowLogoutModal={setShowLogoutModal}
                  />
                </div>

                <NotificationWrapper />

                <div className="relative max-xl:hidden">
                  <Link
                    to={routes.cart}
                    className="w-14 h-14 max-xl:w-[48px] max-xl:h-[48px] grid place-items-center border-2 text-2xl max-xl:text-xl border-[#1D1159] rounded-full bg-radial-gradient duration-150 hover:scale-95"
                    aria-label="Shopping cart"
                  >
                    <i className="ri-shopping-cart-2-line"></i>
                  </Link>
                  {totalCartItems > 0 && (
                    <span
                      role="button"
                      onClick={() => {
                        navigate(routes.cart);
                      }}
                      className="absolute family-mochiy-pop -top-1 -right-1 text-base w-7 h-7 bg-primary rounded-full flex items-center justify-center"
                      aria-label="Total Item"
                    >
                      {formatItemsCount(totalCartItems)}
                    </span>
                  )}
                </div>
              </div>
            )}
            <div className="relative hidden max-xl:block">
              <Link
                to={routes.cart}
                className="w-14 h-14 max-xl:w-[48px] max-xl:h-[48px] grid place-items-center border-2 text-2xl max-xl:text-xl border-[#1D1159] rounded-full bg-radial-gradient duration-150 hover:scale-95"
                aria-label="Shopping cart"
              >
                <i className="ri-shopping-cart-2-line"></i>
              </Link>
              {totalCartItems > 0 && (
                <span
                  role="button"
                  onClick={() => {
                    navigate(routes.cart);
                  }}
                  className="absolute family-mochiy-pop -top-1 -right-1 text-base w-7 h-7 max-md:w-[22px] max-md:h-[22px] max-md:text-sm  bg-primary rounded-full flex items-center justify-center"
                  aria-label="Total item"
                >
                  {formatItemsCount(totalCartItems)}
                </span>
              )}
            </div>
            <div
              className={`bg-black/70 fixed top-0 left-0 h-full w-full z-[9998] sidebar-header ${
                openHeader ? "translate-x-0" : "translate-x-[-100%]"
              }`}
              onClick={() => {
                setOpenHeader(false);
              }}
            >
              <div
                className={`fixed left-0 top-0 w-11/12 py-4 px-6 h-full bg-[#1D1159] gap-6 grid grid-rows-[90px_1fr] max-md:grid-rows-[50px_1fr] text-white text-2xl duration-500 overflow-auto z-[9999] ${
                  openHeader ? "translate-x-0" : "translate-x-[-100%]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <Logo
                    onClick={() => {
                      if (window.location.pathname === "/")
                        window.location.reload();
                      else navigate(routes.default);
                    }}
                    src={FatsuLogo}
                    alt="App-Logo"
                    className="w-44 object-contain max-md:w-24 cursor-pointer"
                  />
                  <button
                    className="duration-150 hover:scale-95"
                    onClick={() => setOpenHeader(!openHeader)}
                    aria-label="Close"
                  >
                    <i className="text-primary text-3xl ri-close-line"></i>
                  </button>
                </div>
                <ul className="space-y-6">
                  <li>
                    <NavLink
                      className={({ isActive }) =>
                        `text-2xl max-md:text-xl block text-primary cursor-pointer duration-200 hover:text-secondary ${
                          isActive ? "text-primary-gradient" : ""
                        }`
                      }
                      onClick={() => setOpenHeader(!openHeader)}
                      to={routes.messages}
                    >
                      Message from fatSu
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      className={({ isActive }) =>
                        `text-2xl max-md:text-xl block text-primary cursor-pointer duration-200 hover:text-secondary ${
                          isActive ? "text-primary-gradient" : ""
                        }`
                      }
                      onClick={() => setOpenHeader(!openHeader)}
                      to={routes.fatsuOrigin}
                    >
                      FatSU’s Origin
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      className={({ isActive }) =>
                        `text-2xl max-md:text-xl block text-primary cursor-pointer duration-200 hover:text-secondary ${
                          isActive ? "text-primary-gradient" : ""
                        }`
                      }
                      onClick={() => setOpenHeader(!openHeader)}
                      to={routes.facts}
                    >
                      Facts, Stats, & Faq
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      className={({ isActive }) =>
                        `text-2xl max-md:text-xl block text-primary cursor-pointer duration-200 hover:text-secondary ${
                          isActive ? "text-primary-gradient" : ""
                        }`
                      }
                      onClick={() => setOpenHeader(!openHeader)}
                      to={routes.metrics}
                    >
                      Metrics
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      onClick={() => setOpenHeader(!openHeader)}
                      className={({ isActive }) =>
                        `text-2xl max-md:text-xl block text-primary cursor-pointer duration-200 hover:text-secondary ${
                          isActive ? "text-primary-gradient" : ""
                        }`
                      }
                      to={routes.shop}
                    >
                      Fatsu Shop
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      onClick={() => setOpenHeader(!openHeader)}
                      className={({ isActive }) =>
                        `text-2xl max-md:text-xl block text-primary cursor-pointer duration-200 hover:text-secondary ${
                          isActive ? "text-primary-gradient" : ""
                        }`
                      }
                      to={routes.news}
                    >
                      Fatsu In Lights
                    </NavLink>
                  </li>
                  {auth && (
                    <>
                      <li>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setmyaccountDropdown(!myaccountDropdown);
                          }}
                          className="w-full flex items-center justify-between text-primary hover:text-secondary cursor-pointer duration-200"
                        >
                          <span className="text-2xl max-md:text-xl">
                            My Account
                          </span>
                          {myaccountDropdown ? (
                            <i className="ri-subtract-fill text-primary-gradient"></i>
                          ) : (
                            <i className="ri-add-line text-primary-gradient"></i>
                          )}
                        </button>
                        {myaccountDropdown && (
                          <ul className="space-y-6 mt-4 ms-6">
                            <li>
                              <NavLink
                                className={({ isActive }) =>
                                  `text-2xl block max-md:text-xl text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary ${
                                    isActive ? "text-primary-gradient" : ""
                                  }`
                                }
                                to={routes.account}
                              >
                                Account
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                className={({ isActive }) =>
                                  `text-2xl block max-md:text-xl text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary ${
                                    isActive ? "text-primary-gradient" : ""
                                  }`
                                }
                                to={routes.purchased}
                              >
                                Purchased Episodes and Music
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                className={({ isActive }) =>
                                  `text-2xl block max-md:text-xl text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary ${
                                    isActive ? "text-primary-gradient" : ""
                                  }`
                                }
                                to={routes.rentedEpisodes}
                              >
                                Rented Episodes
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                className={({ isActive }) =>
                                  `text-2xl block max-md:text-xl text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary ${
                                    isActive ? "text-primary-gradient" : ""
                                  }`
                                }
                                to={routes.orderHistory}
                              >
                                Orders
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                className={({ isActive }) =>
                                  `text-2xl block max-md:text-xl text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary ${
                                    isActive ? "text-primary-gradient" : ""
                                  }`
                                }
                                to={routes.downloads}
                              >
                                Downloads
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                className={({ isActive }) =>
                                  `text-2xl block max-md:text-xl text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary ${
                                    isActive ? "text-primary-gradient" : ""
                                  }`
                                }
                                to={routes.wishlist}
                              >
                                Wishlist
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                className={({ isActive }) =>
                                  `text-2xl block max-md:text-xl text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary ${
                                    isActive ? "text-primary-gradient" : ""
                                  }`
                                }
                                to={routes.bookmark}
                              >
                                Bookmarks
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                className={({ isActive }) =>
                                  `text-2xl block max-md:text-xl text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary ${
                                    isActive ? "text-primary-gradient" : ""
                                  }`
                                }
                                to={routes.addresses}
                              >
                                Addresses
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                className={({ isActive }) =>
                                  `text-2xl block max-md:text-xl text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary ${
                                    isActive ? "text-primary-gradient" : ""
                                  }`
                                }
                                to={routes.paymentmethod}
                              >
                                Payment Methods
                              </NavLink>
                            </li>
                            <li>
                              <NavLink
                                className={({ isActive }) =>
                                  `text-2xl block max-md:text-xl text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary ${
                                    isActive ? "text-primary-gradient" : ""
                                  }`
                                }
                                to={routes.notificationSettings}
                              >
                                Notification Settings
                              </NavLink>
                            </li>
                          </ul>
                        )}
                      </li>
                      <li>
                        <Link
                          onClick={() => setOpenHeader(!openHeader)}
                          className="text-2xl block max-md:text-xl text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary"
                          to={routes.cart}
                        >
                          Cart
                        </Link>
                      </li>
                      <li>
                        <button
                          className="text-2xl block w-full text-start max-md:text-xl text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary"
                          onClick={async (e) => {
                            e.preventDefault();
                            setOpenHeader(!openHeader);
                            setShowLogoutModal(true);
                          }}
                          aria-label="Logout"
                        >
                          Logout
                        </button>
                      </li>
                    </>
                  )}
                  {!auth && (
                    <>
                      <li>
                        <Link
                          onClick={() => setOpenHeader(!openHeader)}
                          className="text-2xl block max-md:text-xl text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary"
                          to={routes.cart}
                        >
                          Cart
                        </Link>
                      </li>
                      <li>
                        <Button
                          onClick={() => {
                            setOpenHeader(!openHeader);
                            setLogin({ type: "updateLogin", payload: true });
                            if (isMobile) {
                              searchParams.set("tab", "login");
                              setSearchParams(searchParams, { replace: true });
                            }
                          }}
                          title="Login"
                          className="text-2xl w-full text-start max-md:text-xl text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary"
                        />
                      </li>
                      <li>
                        <Button
                          onClick={() => {
                            setOpenHeader(!openHeader);
                            setSignup({ type: "updateSignup", payload: true });
                            if (isMobile) {
                              searchParams.set("tab", "signup");
                              setSearchParams(searchParams, { replace: true });
                            }
                          }}
                          title="Sign Up"
                          className="text-2xl max-md:text-xl w-full text-start text-primary hover:opacity-80 cursor-pointer duration-200 hover:text-secondary"
                        />
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </header>

      {!user &&
        (login || (isMobile && searchParams.get("tab") === "login")) && (
          <Login
            loginModal={login || searchParams.get("tab") === "login"}
            setLoginModal={setLogin}
            setSignUpModal={setSignup}
            modalBorder={modalBorder}
            setForgotModal={setForgotModal}
            ref={loginModalRef}
            handleConfirmationRes={(email: string) => {
              setEmail(email);
              setTimeout(() => {
                setIsResentModalOpen(true);
                // in mobile, need to close login modal first and then open confirmation popup
                if (isMobile) {
                  searchParams.delete("tab");
                  setSearchParams(searchParams, { replace: true });
                }
              });
            }}
          />
        )}

      {isResentModalOpen && (
        <div
          className="fixed top-0 left-0 h-full w-full bg-black/50 z-[999] grid place-items-center overflow-auto modal-content resend-modal"
          ref={resendModalref}
        >
          <div className="max-w-2xl max-sm:max-w-[95%] relative z-[1] bg-gradient-to-t from-[#00AEEF] to-[#1D1159] rounded-[0_3rem_3rem_0] max-sm:rounded-[0_2rem_2rem_0]">
            <img
              className="absolute -z-[1] top-0 left-0 h-full w-full"
              src={ModalBorderLarge}
              alt=""
            />
            <div className="p-12 max-md:p-5">
              <button
                className="text-3xl text-primary-gradient absolute top-10 max-sm:top-5 right-10 max-sm:right-5 z-50"
                role="button"
                aria-label="Close"
                onClick={() => {
                  setIsResentModalOpen(false);
                }}
              >
                <i className="ri-close-circle-line"></i>
              </button>
              <h3 className="text-primary text-4xl text-center mb-4 max-sm:text-2xl">
                Confirm Your Email
              </h3>
              <h6 className="text-secondary text-2xl max-sm:text-xl text-center">
                We’ve sent a confirmation email to {email}. Please check your
                inbox.
              </h6>
              <img
                className="h-full w-full max-w-48 mx-auto my-4"
                src={sendMail}
                alt=""
              />
              <div className="text-center">
                <span className="text-white text-2xl max-sm:text-xl block mb-4">
                  Didn’t receive the email?
                </span>
                <button
                  onClick={(e) => {
                    resendConfirmationEmail(e);
                  }}
                  className="max-sm:w-full text-2xl max-sm:text-xl customized-button rounded-[5px_20px_11px_20px] px-40 max-sm:px-7"
                >
                  {isConfirmationLoading ? (
                    <div className="flex items-center justify-center w-full">
                      <i className="ri-loop-right-line btn-loader"></i>
                    </div>
                  ) : (
                    "Resend"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!user &&
        (signup || (isMobile && searchParams.get("tab") === "signup")) && (
          <Signup
            modalBorder={modalBorder}
            setSignUpModal={setSignup}
            signUpModal={signup || searchParams.get("tab") === "signup"}
            setLoginModal={setLogin}
            ref={signupModalRef}
          />
        )}

      {!user &&
        (forgotModal ||
          (isMobile && searchParams.get("tab") === "forgot-password")) && (
          <ForgotPassowrd
            forgotModal={
              forgotModal || searchParams.get("tab") === "forgot-password"
            }
            setForgotModal={setForgotModal}
            modalBorder={modalBorder}
            setLoginModal={setLogin}
            ref={forgotPasswordModalRef}
          />
        )}
      {showUpdatePasswordForm && (
        <NewPasswordForm
          setModal={setShowUpdatePasswordForm}
          token={location.state.token}
          modal={showUpdatePasswordForm}
          ref={newPasswordModalRef}
        />
      )}

      {showLogoutModal && <LogoutModal setModal={setShowLogoutModal} />}
    </>
  );
};

export default Header;
